import React,{useEffect,useRef,useState} from 'react';
import {useAuth} from '../services/authContext';
import {guestRequest} from '../services/guestApi';
import {journalFarm} from '../services/photoJournal';
import {Button,Card} from './ui';
import {presentations} from '../services/presentationCoordinator';
import IndividualPhotoReview from './IndividualPhotoReview';
import ScoutingImage from './ScoutingImage';
import type {ScoutingPhoto} from '../types';

interface ClassificationRun {
 run_id:string;farm_id:string;created_at:string;next_after:string|null;can_apply:boolean;
 counts:{work:number;nature:number;review:number};
 items:{photo_id:string;action:'work'|'nature'|'review';reason:string;label:string;taken_at?:string;image_url:string;publication_revision?:number}[];
 result:null|{work:number;nature:number;review:number;media_failed:string[]};
}
export default function PhotoClassificationReview({onReviewingChange}:{onReviewingChange?:(reviewing:boolean)=>void}){
 const {user,privileges}=useAuth();
 const scope=JSON.stringify([user?.email,user?.status,user?.roles,user?.farm_ids,privileges]);
 const current=useRef(scope);current.current=scope;
 const [state,setState]=useState<{scope:string;farm:string;run:ClassificationRun|null}>({scope:'',farm:'',run:null});
 const [busy,setBusy]=useState(false),[error,setError]=useState('');
 const [selected,setSelected]=useState<{scope:string;id:string}|null>(null);
 const [notice,setNotice]=useState<{scope:string;text:string}|null>(null);
 const admin=user?.status==='Active'&&user.roles?.includes('Admin')&&!!privileges.scouting?.view;
 const visible=state.scope===scope?state:null;
 useEffect(()=>{onReviewingChange?.(!!admin&&selected?.scope===scope);},[admin,selected,scope,onReviewingChange]);
 useEffect(()=>()=>onReviewingChange?.(false),[onReviewingChange]);
 useEffect(()=>{
  let alive=true;setError('');setBusy(false);
  if(!admin)return;
  void journalFarm().then(async farm=>{
   const {runs}=await guestRequest<{runs:ClassificationRun[]}>('/journal/classification/runs?farm_id='+encodeURIComponent(farm.farm_id));
   if(alive&&current.current===scope)setState({scope,farm:farm.farm_id,run:runs[0]||null});
  }).catch(e=>{if(alive&&current.current===scope)setError((e as Error).message);});
  return()=>{alive=false;};
 },[scope,admin]);
 useEffect(()=>{if(busy)return presentations.hold('Back to photo review');},[busy]);
 if(!admin)return null;
 const run=visible?.run;
 const act=async(apply:boolean,after='')=>{
  if(!visible?.farm)return;
  setBusy(true);setError('');
  try{
   const saved=apply&&run
    ?await guestRequest<ClassificationRun>('/journal/classification/'+run.run_id+'/apply','POST',{})
    :await guestRequest<ClassificationRun>('/journal/classification/preview','POST',{farm_id:visible.farm,after});
   if(current.current===scope){setState({scope,farm:visible.farm,run:saved});window.dispatchEvent(new Event('sonata-journal-updated'));}
  }catch(e){if(current.current===scope)setError((e as Error).message);}
  finally{if(current.current===scope)setBusy(false);}
 };
 if(selected?.scope===scope)return <IndividualPhotoReview photoId={selected.id} onClose={()=>{setSelected(null);void act(false);}} onSaved={()=>setNotice({scope,text:'Photo review saved.'})}/>;
 return <Card className="p-5 space-y-4">
  <div><h2 className="text-xl font-semibold">Review older photos</h2>
   <p className="text-sm text-gray-500 mt-1">Resolve ownership and entity associations. Review each photo before choosing to share a copy.</p></div>
  {error&&<p role="alert" className="text-sm text-red-700">{error}</p>}
  {notice?.scope===scope&&<p role="status" className="text-sm text-usda-700">{notice.text}</p>}
  <div className="flex flex-wrap gap-3">
   <Button disabled={busy||!visible?.farm} onClick={()=>{setNotice(null);void act(false);}}>{busy?'Please wait…':'Preview older photos'}</Button>
   {run?.result&&run.next_after&&<Button disabled={busy} onClick={()=>void act(false,run.next_after!)}>Preview next photos</Button>}
  </div>
  {run&&<>
   <p className="text-sm">{run.counts.work} entity photos · {run.counts.review} need individual review{run.counts.nature>0?` · ${run.counts.nature} earlier Nature proposals`:''}</p>
   {run.result?<p role="status" className="text-sm text-usda-700">Recorded: {run.result.work} entity photos. {run.result.review} remain for review.{run.result.nature>0?` This earlier run also published ${run.result.nature} Nature photos.`:''}</p>:<>
    {!run.can_apply&&<p className="text-sm text-gray-500">This preview uses an earlier review policy. Create a fresh preview to continue.</p>}
    {run.can_apply&&run.counts.work>0&&<Button disabled={busy||!privileges.scouting?.write} onClick={()=>void act(true)}>Confirm entity associations</Button>}
   </>}
   <ul className="divide-y">{run.items.map(item=><li key={item.photo_id} className="py-3 flex flex-wrap items-start gap-3">
    <ScoutingImage photo={{photo_id:item.photo_id,image_url:item.image_url||`/api/journal/photos/${encodeURIComponent(item.photo_id)}/media?audience=original`,storage_url:item.image_url||`/api/journal/photos/${encodeURIComponent(item.photo_id)}/media?audience=original`,publication_revision:item.publication_revision} as unknown as ScoutingPhoto} alt="Photo preview" className="w-14 h-14 rounded-lg object-cover shrink-0"/>
    <div className="min-w-0"><p className="text-sm font-medium">{item.label}{item.taken_at?' · '+new Date(item.taken_at).toLocaleDateString():''}</p>
     <p className="text-sm text-gray-500">{item.reason}</p>
     <p className="text-xs text-gray-500 mt-1">{item.action==='review'?'Needs review':item.action==='work'?'Entity photo':'Earlier Nature proposal — review individually'}</p></div>
    <button type="button" disabled={busy} onClick={()=>setSelected({scope,id:item.photo_id})} className="text-sm text-usda-700 py-2 sm:ml-auto">Review photo</button>
   </li>)}</ul>
   {run.items.length===0&&<p className="text-sm text-gray-500">No photos need classification in this part of the library.</p>}
  </>}
 </Card>;
}
